import React from 'react';
import { withRouter } from 'react-router-dom';

// mui
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import BackButton from 'components/v2/PageElements/BackButton';

import Avatar from './Avatar';

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'contained',
})(({ theme, variant, contained }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: theme.spacing(1),
  padding: theme.spacing(1),
  maxWidth: variant === 'detail' ? '45rem' : 'calc(100vw - 170px)',
  ...(contained || variant === 'detail' ? { paddingRight: theme.spacing(2) } : {}),
}));

const TitleBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasSubtitle',
})(({ theme, hasSubtitle }) => ({
  color: theme.palette.text.primary,
  fontSize: hasSubtitle ? '1.5rem' : '2rem',
  fontWeight: hasSubtitle ? 300 : 100,
  padding: 0,
  margin: 0,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  fontWeight: 300,
  padding: 0,
  margin: 0,
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginLeft: 'auto',
  '& > *': {
    marginLeft: theme.spacing(0.5),
  },
}));

const ImageIcon = styled('img', {
  shouldForwardProp: (prop) => prop !== 'updatedIcon' && prop !== 'rounded',
})(({ theme, updatedIcon, rounded }) => ({
  height: updatedIcon ? '2.5rem' : '2rem',
  width: updatedIcon ? '2.5rem' : '2rem',
  marginRight: '0.75rem',
  borderRadius: rounded ? 16 : theme.shape.borderRadius,
  display: 'inline-block',
}));

const MuiIconWrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== 'updatedIcon' && prop !== 'rounded',
})(({ theme, updatedIcon, rounded }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: updatedIcon ? '2.5rem' : '2rem',
  width: updatedIcon ? '2.5rem' : '2rem',
  marginRight: '0.75rem',
  borderRadius: rounded ? 16 : theme.shape.borderRadius,
  ...(!updatedIcon && { backgroundColor: theme.palette.text.secondary }),
  color: theme.palette.common.white,
  padding: '0.25rem',
  fontSize: updatedIcon ? '2rem' : '1.5rem',
}));

const Icon = ({ icon, rounded, updatedIcon }) => {
  if (!icon) return null;

  if (typeof icon === 'string') {
    return <ImageIcon src={icon} alt="header icon" updatedIcon={updatedIcon} rounded={rounded} />;
  }

  return (
    <MuiIconWrapper rounded={rounded} updatedIcon={updatedIcon}>
      {React.cloneElement(icon, {
        fontSize: 'inherit',
      })}
    </MuiIconWrapper>
  );
};

export default withRouter(
  ({
    icon,
    updatedIcon,
    title,
    subtitle,
    children,
    rounded = false,
    history,
    backPath,
    backText,
    preferHistoryGoBack = false,
    variant,
    contained,
    user,
  }) => {
    const handleBackButtonClick = (event) => {
      if (event) event.preventDefault();
      if (!history) return;

      // make sure there is history to go back to. A new tab and pasted URL will give a length of 2
      if (preferHistoryGoBack && history.length > 2) {
        return history.goBack();
      }

      history.push(backPath);
    };

    const backButtonProps = {
      onClick: handleBackButtonClick,
      text: backText,
    };

    return (
      <>
        {backPath && <BackButton {...backButtonProps} />}

        <Container variant={variant} contained={contained}>
          {title && (
            <>
              {user ? (
                <Avatar {...{ user, profile: user }} />
              ) : (
                <Icon {...{ icon, rounded, updatedIcon }} />
              )}

              <TitleBlock>
                <Title variant="h2" hasSubtitle={!!subtitle}>
                  {title}
                </Title>

                {subtitle && <Subtitle variant="h3">{subtitle}</Subtitle>}
              </TitleBlock>
            </>
          )}

          <ActionContainer>{children}</ActionContainer>
        </Container>
      </>
    );
  }
);
